import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import {
  ECommerceProductsState,
  Product,
  QueryProductDto,
  GrabDealDto,
  GrabCouponDto,
  GrabPromotionDto,
  GrabAuraDiscountDto,
  PublicProductQueryDto,
  PublicProductResponse,
  ProductListResponse,
  ProductDetailResponse,
  BrandDetailResponse,
  BrandWithProducts,
  ApiResponse,
  ECOMMERCE_PRODUCTS_ACTION_TYPES,
} from "./types";

// Base URL for the API
const API_BASE_URL = [
  process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_ECOMMERCE_PERFIX,
]
  .filter(Boolean)
  .join("");

if (!process.env.REACT_APP_API_BASE_URL) {
  console.warn("Warning: REACT_APP_API_BASE_URL is not defined.");
}
if (!process.env.REACT_APP_ECOMMERCE_PERFIX) {
  console.warn("Warning: REACT_APP_ECOMMERCE_PERFIX is not defined.");
}
// Initial state
const initialState: ECommerceProductsState = {
  products: [],
  currentProduct: null,
  currentBrand: null,
  productsWithCoupons: [],
  productsWithDeals: [],
  productsWithPromotions: [],
  productsWithBuyXGetYPromotions: [],
  productsWithSalarAuraExclusive: [],
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  couponsPagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  dealsPagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  promotionsPagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  buyXGetYPromotionsPagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  auraExclusivePagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  publicProducts: [],
  sponsoredSlots: [],
  publicPagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },
  filters: {
    brands: [],
    colors: [],
    maxPriceAvailable: 0,
  },
  loading: {
    products: false,
    product: false,
    productsWithCoupons: false,
    productsWithDeals: false,
    productsWithPromotions: false,
    productsWithBuyXGetYPromotions: false,
    productsWithSalarAuraExclusive: false,
    brand: false,
    grabDeal: false,
    grabCoupon: false,
    grabPromotion: false,
    grabAuraDiscount: false,
    publicProducts: false,
  },
  error: null,
};

// Async thunks for API calls

/**
 * Fetch all products with pagination and filters
 */
export const fetchProducts = createAsyncThunk<
  ProductListResponse,
  QueryProductDto,
  { rejectValue: string }
>(
  ECOMMERCE_PRODUCTS_ACTION_TYPES.FETCH_PRODUCTS_START,
  async (queryParams, { rejectWithValue }) => {
    try {
      const response = await axios.get<ApiResponse<ProductListResponse>>(
        `${API_BASE_URL}/e-commerce/products`,
        { params: queryParams },
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch products",
      );
    }
  },
);

/**
 * Fetch product by ID
 */
export const fetchProductById = createAsyncThunk<
  Product,
  string,
  { rejectValue: string }
>(
  ECOMMERCE_PRODUCTS_ACTION_TYPES.FETCH_PRODUCT_BY_ID_START,
  async (productId, { rejectWithValue }) => {
    try {
      const response = await axios.get<ProductDetailResponse>(
        `${API_BASE_URL}/e-commerce/products/${productId}`,
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch product",
      );
    }
  },
);

/**
 * Fetch brand by ID with its products
 */
export const fetchBrandById = createAsyncThunk<
  BrandWithProducts,
  string,
  { rejectValue: string }
>(
  ECOMMERCE_PRODUCTS_ACTION_TYPES.FETCH_BRAND_BY_ID_START,
  async (brandId, { rejectWithValue }) => {
    try {
      const response = await axios.get<BrandDetailResponse>(
        `${API_BASE_URL}/e-commerce/brands/${brandId}`,
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch brand details",
      );
    }
  },
);

/**
 * Fetch products with active coupons
 */
export const fetchProductsWithCoupons = createAsyncThunk<
  ProductListResponse,
  QueryProductDto,
  { rejectValue: string }
>(
  ECOMMERCE_PRODUCTS_ACTION_TYPES.FETCH_PRODUCTS_WITH_COUPONS_START,
  async (queryParams, { rejectWithValue }) => {
    try {
      const response = await axios.get<ApiResponse<ProductListResponse>>(
        `${API_BASE_URL}/e-commerce/products/with-coupons`,
        { params: queryParams },
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch products with coupons",
      );
    }
  },
);

/**
 * Fetch products with active deals
 */
export const fetchProductsWithDeals = createAsyncThunk<
  ProductListResponse,
  QueryProductDto,
  { rejectValue: string }
>(
  ECOMMERCE_PRODUCTS_ACTION_TYPES.FETCH_PRODUCTS_WITH_DEALS_START,
  async (queryParams, { rejectWithValue }) => {
    try {
      const response = await axios.get<ApiResponse<ProductListResponse>>(
        `${API_BASE_URL}/e-commerce/products/with-deals`,
        { params: queryParams },
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch products with deals",
      );
    }
  },
);

/**
 * Fetch products with active promotions
 */
export const fetchProductsWithPromotions = createAsyncThunk<
  ProductListResponse,
  QueryProductDto,
  { rejectValue: string }
>(
  ECOMMERCE_PRODUCTS_ACTION_TYPES.FETCH_PRODUCTS_WITH_PROMOTIONS_START,
  async (queryParams, { rejectWithValue }) => {
    try {
      const response = await axios.get<ApiResponse<ProductListResponse>>(
        `${API_BASE_URL}/e-commerce/products/with-promotions`,
        { params: queryParams },
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch products with promotions",
      );
    }
  },
);

/**
 * Fetch products with BUY_X_GET_Y_FREE promotions
 */
export const fetchProductsWithBuyXGetYPromotions = createAsyncThunk<
  ProductListResponse,
  QueryProductDto,
  { rejectValue: string }
>(
  ECOMMERCE_PRODUCTS_ACTION_TYPES.FETCH_PRODUCTS_WITH_BUY_X_GET_Y_PROMOTIONS_START,
  async (queryParams, { rejectWithValue }) => {
    try {
      const response = await axios.get<ApiResponse<ProductListResponse>>(
        `${API_BASE_URL}/e-commerce/products/with-buy-x-get-y-promotions`,
        { params: queryParams },
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch products with BUY_X_GET_Y_FREE promotions",
      );
    }
  },
);

/**
 * Fetch products with SalarAura exclusive discounts
 */
export const fetchProductsWithSalarAuraExclusive = createAsyncThunk<
  ProductListResponse,
  QueryProductDto,
  { rejectValue: string }
>(
  ECOMMERCE_PRODUCTS_ACTION_TYPES.FETCH_PRODUCTS_WITH_SALARAURA_EXCLUSIVE_START,
  async (queryParams, { rejectWithValue }) => {
    try {
      const response = await axios.get<ApiResponse<ProductListResponse>>(
        `${API_BASE_URL}/e-commerce/products/with-salaraura-exclusive`,
        { params: queryParams },
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch products with SalarAura exclusive discounts",
      );
    }
  },
);

/**
 * Fetch public products with filters
 */
export const fetchPublicProducts = createAsyncThunk<
  PublicProductResponse,
  PublicProductQueryDto,
  { rejectValue: string }
>(
  ECOMMERCE_PRODUCTS_ACTION_TYPES.FETCH_PUBLIC_PRODUCTS_START,
  async (queryParams, { rejectWithValue }) => {
    try {
      const response = await axios.get<PublicProductResponse>(
        `${API_BASE_URL}/e-commerce/products/public`,
        { params: queryParams },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch public products",
      );
    }
  },
);

/**
 * Grab/Redeem a deal
 */
export const grabDeal = createAsyncThunk<
  any,
  GrabDealDto,
  { rejectValue: string }
>(
  ECOMMERCE_PRODUCTS_ACTION_TYPES.GRAB_DEAL_START,
  async (grabDealDto, { rejectWithValue }) => {
    try {
      const response = await axios.post<ApiResponse<any>>(
        `${API_BASE_URL}/e-commerce/deals/grab`,
        grabDealDto,
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to grab deal",
      );
    }
  },
);

/**
 * Grab/Redeem a coupon
 */
export const grabCoupon = createAsyncThunk<
  any,
  { couponId: string; grabCouponDto: GrabCouponDto },
  { rejectValue: string }
>(
  ECOMMERCE_PRODUCTS_ACTION_TYPES.GRAB_COUPON_START,
  async ({ couponId, grabCouponDto }, { rejectWithValue }) => {
    try {
      const response = await axios.post<ApiResponse<any>>(
        `${API_BASE_URL}/e-commerce/coupons/${couponId}/grab`,
        grabCouponDto,
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to grab coupon",
      );
    }
  },
);

/**
 * Grab/Redeem a promotion
 */
export const grabPromotion = createAsyncThunk<
  any,
  { promotionId: string; grabPromotionDto: GrabPromotionDto },
  { rejectValue: string }
>(
  ECOMMERCE_PRODUCTS_ACTION_TYPES.GRAB_PROMOTION_START,
  async ({ promotionId, grabPromotionDto }, { rejectWithValue }) => {
    try {
      const response = await axios.post<ApiResponse<any>>(
        `${API_BASE_URL}/e-commerce/promotions/${promotionId}/grab`,
        grabPromotionDto,
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to grab promotion",
      );
    }
  },
);

/**
 * Grab/Redeem a SalarAura exclusive discount
 */
export const grabAuraDiscount = createAsyncThunk<
  any,
  GrabAuraDiscountDto,
  { rejectValue: string }
>(
  ECOMMERCE_PRODUCTS_ACTION_TYPES.GRAB_AURA_DISCOUNT_START,
  async (grabAuraDiscountDto, { rejectWithValue }) => {
    try {
      const response = await axios.post<ApiResponse<any>>(
        `${API_BASE_URL}/e-commerce/salaraura-discounts/grab`,
        grabAuraDiscountDto,
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to grab SalarAura discount",
      );
    }
  },
);

// Slice
const eCommerceProductsSlice = createSlice({
  name: "eCommerceProducts",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
    },
    clearCurrentBrand: (state) => {
      state.currentBrand = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Products
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.loading.products = true;
        state.error = null;
      })
      .addCase(
        fetchProducts.fulfilled,
        (state, action: PayloadAction<ProductListResponse>) => {
          state.loading.products = false;
          state.products = action.payload.data;
          state.sponsoredSlots = action.payload.sponsoredSlots ?? [];
          state.pagination = {
            page: action.payload.page,
            limit: action.payload.limit,
            total: action.payload.total,
            totalPages: action.payload.totalPages,
          };
        },
      )
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading.products = false;
        state.error = action.payload || "Failed to fetch products";
      });

    // Fetch Product By ID
    builder
      .addCase(fetchProductById.pending, (state) => {
        state.loading.product = true;
        state.error = null;
      })
      .addCase(
        fetchProductById.fulfilled,
        (state, action: PayloadAction<Product>) => {
          state.loading.product = false;
          state.currentProduct = action.payload;
        },
      )
      .addCase(fetchProductById.rejected, (state, action) => {
        state.loading.product = false;
        state.error = action.payload || "Failed to fetch product";
      });

    // Fetch Brand By ID
    builder
      .addCase(fetchBrandById.pending, (state) => {
        state.loading.brand = true;
        state.error = null;
      })
      .addCase(
        fetchBrandById.fulfilled,
        (state, action: PayloadAction<BrandWithProducts>) => {
          state.loading.brand = false;
          state.currentBrand = action.payload;
        },
      )
      .addCase(fetchBrandById.rejected, (state, action) => {
        state.loading.brand = false;
        state.error = action.payload || "Failed to fetch brand details";
      });

    // Fetch Products With Coupons
    builder
      .addCase(fetchProductsWithCoupons.pending, (state) => {
        state.loading.productsWithCoupons = true;
        state.error = null;
      })
      .addCase(
        fetchProductsWithCoupons.fulfilled,
        (state, action: PayloadAction<ProductListResponse>) => {
          state.loading.productsWithCoupons = false;
          state.productsWithCoupons = action.payload.data;
          state.couponsPagination = {
            page: action.payload.page,
            limit: action.payload.limit,
            total: action.payload.total,
            totalPages: action.payload.totalPages,
          };
        },
      )
      .addCase(fetchProductsWithCoupons.rejected, (state, action) => {
        state.loading.productsWithCoupons = false;
        state.error = action.payload || "Failed to fetch products with coupons";
      });

    // Fetch Products With Deals
    builder
      .addCase(fetchProductsWithDeals.pending, (state) => {
        state.loading.productsWithDeals = true;
        state.error = null;
      })
      .addCase(
        fetchProductsWithDeals.fulfilled,
        (state, action: PayloadAction<ProductListResponse>) => {
          state.loading.productsWithDeals = false;
          state.productsWithDeals = action.payload.data;
          state.dealsPagination = {
            page: action.payload.page,
            limit: action.payload.limit,
            total: action.payload.total,
            totalPages: action.payload.totalPages,
          };
        },
      )
      .addCase(fetchProductsWithDeals.rejected, (state, action) => {
        state.loading.productsWithDeals = false;
        state.error = action.payload || "Failed to fetch products with deals";
      });

    // Fetch Products With Promotions
    builder
      .addCase(fetchProductsWithPromotions.pending, (state) => {
        state.loading.productsWithPromotions = true;
        state.error = null;
      })
      .addCase(
        fetchProductsWithPromotions.fulfilled,
        (state, action: PayloadAction<ProductListResponse>) => {
          state.loading.productsWithPromotions = false;
          state.productsWithPromotions = action.payload.data;
          state.promotionsPagination = {
            page: action.payload.page,
            limit: action.payload.limit,
            total: action.payload.total,
            totalPages: action.payload.totalPages,
          };
        },
      )
      .addCase(fetchProductsWithPromotions.rejected, (state, action) => {
        state.loading.productsWithPromotions = false;
        state.error =
          action.payload || "Failed to fetch products with promotions";
      });

    // Fetch Products With Buy X Get Y Promotions
    builder
      .addCase(fetchProductsWithBuyXGetYPromotions.pending, (state) => {
        state.loading.productsWithBuyXGetYPromotions = true;
        state.error = null;
      })
      .addCase(
        fetchProductsWithBuyXGetYPromotions.fulfilled,
        (state, action: PayloadAction<ProductListResponse>) => {
          state.loading.productsWithBuyXGetYPromotions = false;
          state.productsWithBuyXGetYPromotions = action.payload.data;
          state.buyXGetYPromotionsPagination = {
            page: action.payload.page,
            limit: action.payload.limit,
            total: action.payload.total,
            totalPages: action.payload.totalPages,
          };
        },
      )
      .addCase(
        fetchProductsWithBuyXGetYPromotions.rejected,
        (state, action) => {
          state.loading.productsWithBuyXGetYPromotions = false;
          state.error =
            action.payload ||
            "Failed to fetch products with BUY_X_GET_Y_FREE promotions";
        },
      );

    // Fetch Products With SalarAura Exclusive
    builder
      .addCase(fetchProductsWithSalarAuraExclusive.pending, (state) => {
        state.loading.productsWithSalarAuraExclusive = true;
        state.error = null;
      })
      .addCase(
        fetchProductsWithSalarAuraExclusive.fulfilled,
        (state, action: PayloadAction<ProductListResponse>) => {
          state.loading.productsWithSalarAuraExclusive = false;
          state.productsWithSalarAuraExclusive = action.payload.data;
          state.auraExclusivePagination = {
            page: action.payload.page,
            limit: action.payload.limit,
            total: action.payload.total,
            totalPages: action.payload.totalPages,
          };
        },
      )
      .addCase(
        fetchProductsWithSalarAuraExclusive.rejected,
        (state, action) => {
          state.loading.productsWithSalarAuraExclusive = false;
          state.error =
            action.payload ||
            "Failed to fetch products with SalarAura exclusive discounts";
        },
      );

    // Grab Deal
    builder
      .addCase(grabDeal.pending, (state) => {
        state.loading.grabDeal = true;
        state.error = null;
      })
      .addCase(grabDeal.fulfilled, (state) => {
        state.loading.grabDeal = false;
      })
      .addCase(grabDeal.rejected, (state, action) => {
        state.loading.grabDeal = false;
        state.error = action.payload || "Failed to grab deal";
      });

    // Grab Coupon
    builder
      .addCase(grabCoupon.pending, (state) => {
        state.loading.grabCoupon = true;
        state.error = null;
      })
      .addCase(grabCoupon.fulfilled, (state) => {
        state.loading.grabCoupon = false;
      })
      .addCase(grabCoupon.rejected, (state, action) => {
        state.loading.grabCoupon = false;
        state.error = action.payload || "Failed to grab coupon";
      });

    // Grab Promotion
    builder
      .addCase(grabPromotion.pending, (state) => {
        state.loading.grabPromotion = true;
        state.error = null;
      })
      .addCase(grabPromotion.fulfilled, (state) => {
        state.loading.grabPromotion = false;
      })
      .addCase(grabPromotion.rejected, (state, action) => {
        state.loading.grabPromotion = false;
        state.error = action.payload || "Failed to grab promotion";
      });

    // Grab Aura Discount
    builder
      .addCase(grabAuraDiscount.pending, (state) => {
        state.loading.grabAuraDiscount = true;
        state.error = null;
      })
      .addCase(grabAuraDiscount.fulfilled, (state) => {
        state.loading.grabAuraDiscount = false;
      })
      .addCase(grabAuraDiscount.rejected, (state, action) => {
        state.loading.grabAuraDiscount = false;
        state.error = action.payload || "Failed to grab SalarAura discount";
      });

    // Fetch Public Products
    builder
      .addCase(fetchPublicProducts.pending, (state) => {
        state.loading.publicProducts = true;
        state.error = null;
      })
      .addCase(
        fetchPublicProducts.fulfilled,
        (state, action: PayloadAction<PublicProductResponse>) => {
          state.loading.publicProducts = false;
          state.publicProducts = action.payload.data.products;
          state.sponsoredSlots = action.payload.data.sponsoredSlots ?? [];
          state.publicPagination = {
            page: action.payload.data.pagination.currentPage,
            limit: action.payload.data.pagination.limit,
            total: action.payload.data.pagination.totalItems,
            totalPages: action.payload.data.pagination.totalPages,
          };
          state.filters = action.payload.data.filters;
        },
      )
      .addCase(fetchPublicProducts.rejected, (state, action) => {
        state.loading.publicProducts = false;
        state.error = action.payload || "Failed to fetch public products";
      });
  },
});

export const { clearError, clearCurrentProduct, clearCurrentBrand } =
  eCommerceProductsSlice.actions;
export default eCommerceProductsSlice.reducer;
